
import React from "react";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { UseFormReturn } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle } from "lucide-react";

interface MentorFormProps {
  form: UseFormReturn<any>;
}

const MentorFormStep3: React.FC<MentorFormProps> = ({ form }) => {
  const { watch } = form;
  const values = watch();
  
  return (
    <Form {...form}>
      <div className="space-y-6">
        <h2 className="text-xl font-semibold mb-4">Review & Submit</h2>
        
        <div className="space-y-6">
          <Card>
            <CardContent className="pt-6">
              <h3 className="font-medium mb-2">Personal Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-sm text-muted-foreground">Full Name</div>
                  <div className="font-medium">{values.full_name || "Not provided"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Current Position</div>
                  <div className="font-medium">{values.title || "Not provided"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Company/Organization</div>
                  <div className="font-medium">{values.company || "Not provided"}</div>
                </div>
                <div>
                  <div className="text-sm text-muted-foreground">Years of Experience</div>
                  <div className="font-medium">{values.experience_years || "Not provided"}</div>
                </div>
              </div>
              
              <h3 className="font-medium mb-2">Bio</h3>
              <p className="bg-muted/50 p-3 rounded-md mb-4 whitespace-pre-wrap">
                {values.bio || "No bio provided"}
              </p>
              
              <h3 className="font-medium mb-2">Areas of Expertise</h3>
              <div className="flex flex-wrap gap-2 mb-4">
                {values.expertise ? (
                  values.expertise.split(',').map((item: string, index: number) => (
                    <span 
                      key={index}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                    >
                      <CheckCircle className="mr-1 h-3 w-3" />
                      {item.trim()}
                    </span>
                  ))
                ) : (
                  <span className="text-muted-foreground">No expertise provided</span>
                )}
              </div>
              
              <h3 className="font-medium mb-2">Availability</h3>
              <p className="bg-muted/50 p-3 rounded-md">
                {values.availability || "No availability provided"}
              </p>
            </CardContent>
          </Card>
          
          <FormField
            control={form.control}
            name="calendly_url"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Calendly URL (Optional)</FormLabel>
                <FormControl>
                  <Input 
                    placeholder="https://calendly.com/yourusername" 
                    {...field} 
                  />
                </FormControl>
                <FormDescription>
                  Add your Calendly link to make it easy for students to schedule time with you
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
          
          <FormField
            control={form.control}
            name="beta_tester"
            render={({ field }) => (
              <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                <FormControl>
                  <input
                    type="checkbox"
                    checked={field.value}
                    onChange={field.onChange}
                    className="h-4 w-4 mt-1"
                  />
                </FormControl>
                <div className="space-y-1 leading-none">
                  <FormLabel>I agree to TeenVisor's mentorship guidelines</FormLabel>
                  <FormDescription>
                    By checking this box, I agree to follow TeenVisor's mentorship guidelines and code of conduct
                  </FormDescription>
                </div>
              </FormItem>
            )}
          />
        </div>
      </div>
    </Form>
  );
};

export default MentorFormStep3;
